import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

type ChartData = {
  name: string;
  value: number;
};

interface PieChartComponentProps {
  title: string;
  data: ChartData[];
  hireConversionRate?: number;
  colordata?: string[]; // Optional legacy prop; status colors take priority.
}

const STATUS_ORDER = ["ACCEPTED", "REJECTED", "PLACED", "DRAFTED", "EDITED"];

const normalizeStatus = (name: string) => name.trim().toUpperCase();

const INNER_RADIUS = 80;
const OUTER_RADIUS = 140;

const STATUS_COLORS: Record<string, string> = {
  ACCEPTED: "#377DDE", // Blue
  REJECTED: "#EB3349", // Red
  PLACED: "#85CD74", // Green
  DRAFTED: "#FFDE5C", // Yellow
  EDITED: "#BFC9F2", // Purple
};

  // "BFC9F2",
  //           "FFDE5C",
  //           "85CD74",
  //           "EB3349",
  //           "377DDE",

const getStatusColor = (name: string): string =>
  STATUS_COLORS[normalizeStatus(name)] ?? "#9CA3AF";

const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  value,
  innerRadius,
  outerRadius,
}: {
  cx: number;
  cy: number;
  midAngle: number;
  value: number;
  innerRadius: number;
  outerRadius: number;
}) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180);
  const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180);

  return (
    <text
      x={x}
      y={y}
      fill="#FFFFFF"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={15}
      fontWeight={700}
    >
      {value}
    </text>
  );
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: ChartData }>;
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;

  const { name, value } = payload[0].payload;
  const color = getStatusColor(name);

  return (
    <div
      className="rounded shadow"
      style={{
        padding: "8px 12px",
        color: "#FFFFFF",
        backgroundColor: color,
        border: `1px solid ${color}`,
      }}
    >
      {name}: {value}
    </div>
  );
};

const PieCharts: React.FC<PieChartComponentProps> = ({
  title,
  data,
  hireConversionRate,
}) => {
  // Keep the same clockwise slice and legend order even when the API order changes.
  const orderedData = [...data].sort((a, b) => {
    const aIndex = STATUS_ORDER.indexOf(normalizeStatus(a.name));
    const bIndex = STATUS_ORDER.indexOf(normalizeStatus(b.name));
    return (aIndex === -1 ? STATUS_ORDER.length : aIndex) -
      (bIndex === -1 ? STATUS_ORDER.length : bIndex);
  });
  const isSingleData = orderedData.length === 1;

  return (
    <div className="pt-8 px-4 pb-4 rounded-xl border">
      <div className="header flex items-center justify-between py-2 border-b">
        <h2 className="text-sm font-semibold text-gray-600 mb-4">
          {title}
        </h2>

        {hireConversionRate !== undefined && (
          <div className="flex justify-end mb-3">
            <h1 className="text-sm font-semibold text-gray-600">
              Hire Conversion Rate: {hireConversionRate.toFixed(2)}%
            </h1>
          </div>
        )}
      </div>

      <div className="bg-white w-full">
        <div className="flex items-center justify-between flex-wrap px-5">
          <div style={{ width: "30%", minWidth: "300px", height: "240px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={orderedData}
                  dataKey="value"
                  nameKey="name"
                  startAngle={180}
                  endAngle={0}
                  cx="50%"
                  cy="75%"
                  innerRadius={INNER_RADIUS}
                  outerRadius={OUTER_RADIUS}
                  paddingAngle={isSingleData ? 0 : 2}
                  labelLine={false}
                  label={renderCustomizedLabel}
                >
                  {orderedData.map((item, index) => (
                    <Cell
                      key={`${item.name}-${index}`}
                      fill={getStatusColor(item.name)}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={{ width: "20%", minWidth: "220px" }}>
            {orderedData.map((item, index) => (
              <div
                key={`${item.name}-${index}`}
                className="flex items-center"
                style={{ marginBottom: "18px" }}
              >
                <span
                  className="text-sm text-gray-700"
                  style={{ width: "90px" }}
                >
                  {item.name}
                </span>
                <div
                  style={{
                    flex: 1,
                    height: "10px",
                    borderRadius: "999px",
                    backgroundColor: getStatusColor(item.name),
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PieCharts;
